export class DashSim {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DashSimFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_dashsim_free(ptr, 0);
    }
    /**
     * @returns {bigint}
     */
    distance_px() {
        const ret = wasm.dashsim_distance_px(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Current FSM form (DarioState as u32).
     * @returns {number}
     */
    form() {
        const ret = wasm.dashsim_form(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @returns {boolean}
     */
    grounded() {
        const ret = wasm.dashsim_grounded(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * @returns {boolean}
     */
    invulnerable() {
        const ret = wasm.dashsim_invulnerable(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * @returns {number}
     */
    kills() {
        const ret = wasm.dashsim_kills(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @param {bigint} seed
     */
    constructor(seed) {
        const ret = wasm.dashsim_new(seed);
        this.__wbg_ptr = ret;
        DashSimFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * @returns {boolean}
     */
    over() {
        const ret = wasm.dashsim_over(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * @returns {number}
     */
    pickups() {
        const ret = wasm.dashsim_pickups(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Player rect in pixels: [x, y, w, h].
     * @returns {Int32Array}
     */
    player() {
        const ret = wasm.dashsim_player(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * @returns {bigint}
     */
    score() {
        const ret = wasm.dashsim_score(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Flat entity records: [entity_type, kind, x, y, w, h] per entity.
     * entity_type: 0 obstacle, 1 item, 2 fireball.
     * @returns {Int32Array}
     */
    snapshot() {
        const ret = wasm.dashsim_snapshot(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Advances one 60Hz tick, recording `input` (bit0 jump, bit1 fire).
     * @param {number} input
     */
    tick(input) {
        wasm.dashsim_tick(this.__wbg_ptr, input);
    }
    /**
     * @returns {number}
     */
    ticks() {
        const ret = wasm.dashsim_ticks(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The recorded input trace (one byte per tick), for proving.
     * @returns {Uint8Array}
     */
    trace() {
        const ret = wasm.dashsim_trace(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) DashSim.prototype[Symbol.dispose] = DashSim.prototype.free;

/**
 * wasm wrapper around the 30 Hz browser-provable `dash_zk` sim.
 *
 * Same JS-facing API as [`DashSim`], plus [`ZkDashSim::input_json`], which
 * serializes the full circom witness input for snarkjs `fullProve` after a
 * finished run — everything needed to prove entirely in-browser.
 */
export class ZkDashSim {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ZkDashSimFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_zkdashsim_free(ptr, 0);
    }
    /**
     * @returns {bigint}
     */
    distance_px() {
        const ret = wasm.zkdashsim_distance_px(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Current FSM form (DarioState as u32).
     * @returns {number}
     */
    form() {
        const ret = wasm.zkdashsim_form(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @returns {boolean}
     */
    grounded() {
        const ret = wasm.zkdashsim_grounded(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The full snarkjs circuit input (publics + private witness) for the
     * finished run. `acct_hex` is the caller's 96-byte Moonlight account
     * (192 hex chars), or empty to leave the account unbound (zeros).
     * @param {string} acct_hex
     * @returns {string}
     */
    input_json(acct_hex) {
        let deferred3_0;
        let deferred3_1;
        try {
            const ptr0 = passStringToWasm0(acct_hex, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len0 = WASM_VECTOR_LEN;
            const ret = wasm.zkdashsim_input_json(this.__wbg_ptr, ptr0, len0);
            var ptr2 = ret[0];
            var len2 = ret[1];
            if (ret[3]) {
                ptr2 = 0; len2 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred3_0 = ptr2;
            deferred3_1 = len2;
            return getStringFromWasm0(ptr2, len2);
        } finally {
            wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
        }
    }
    /**
     * @returns {boolean}
     */
    invulnerable() {
        const ret = wasm.zkdashsim_invulnerable(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * @returns {number}
     */
    kills() {
        const ret = wasm.zkdashsim_kills(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @param {bigint} seed
     */
    constructor(seed) {
        const ret = wasm.zkdashsim_new(seed);
        this.__wbg_ptr = ret;
        ZkDashSimFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * @returns {boolean}
     */
    over() {
        const ret = wasm.zkdashsim_over(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * @returns {number}
     */
    pickups() {
        const ret = wasm.zkdashsim_pickups(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Player rect in pixels: [x, y, w, h].
     * @returns {Int32Array}
     */
    player() {
        const ret = wasm.zkdashsim_player(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * @returns {bigint}
     */
    score() {
        const ret = wasm.zkdashsim_score(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Flat entity records: [entity_type, kind, x, y, w, h] per entity.
     * entity_type: 0 obstacle, 1 item, 2 fireball.
     * @returns {Int32Array}
     */
    snapshot() {
        const ret = wasm.zkdashsim_snapshot(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Advances one 30Hz tick, recording `input` (bit0 jump, bit1 fire).
     * @param {number} input
     */
    tick(input) {
        wasm.zkdashsim_tick(this.__wbg_ptr, input);
    }
    /**
     * @returns {number}
     */
    ticks() {
        const ret = wasm.zkdashsim_ticks(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The recorded input trace (one byte per tick).
     * @returns {Uint8Array}
     */
    trace() {
        const ret = wasm.zkdashsim_trace(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ZkDashSim.prototype[Symbol.dispose] = ZkDashSim.prototype.free;
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg_Error_92b29b0548f8b746: function(arg0, arg1) {
            const ret = Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg___wbindgen_throw_344f42d3211c4765: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./dash_web_bg.js": import0,
    };
}

const DashSimFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_dashsim_free(ptr, 1));
const ZkDashSimFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_zkdashsim_free(ptr, 1));

function getArrayI32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getInt32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedInt32ArrayMemory0 = null;
function getInt32ArrayMemory0() {
    if (cachedInt32ArrayMemory0 === null || cachedInt32ArrayMemory0.byteLength === 0) {
        cachedInt32ArrayMemory0 = new Int32Array(wasm.memory.buffer);
    }
    return cachedInt32ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedInt32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = module.ok && expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('dash_web_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
